import { i18n } from '@/shared/i18n';
import { type TaxonomyFieldGroup, type TaxonomyFieldType } from '@/entities/taxonomy';
import { type SchemaProblem } from '../../lib/validateSchema';

/** Generated columns (computed Variable + linked join key) have no editable cells. */
export const isGeneratedType = (t: TaxonomyFieldType) => t === 'computed' || t === 'linked';

/**
 * A problem the details popover asked the grid to show.
 *
 * `nonce` is what makes the request repeatable: clicking the same problem twice
 * must scroll to it twice, and a prop that compares equal would be a no-op the
 * second time.
 */
export interface FocusTarget {
  fieldId: string;
  /** Absent for a column-level finding — those focus the header, not a row. */
  row?: number;
  column?: 'value' | 'code';
  nonce: number;
}

/** A dialog or menu action rewrites a column in one gesture, so it is checked at once. */
export const IMMEDIATE = { immediate: true };

/** One tooltip line per problem: what's wrong, then what to do. */
export function describeProblems(problems: SchemaProblem[] | undefined): string | null {
  if (!problems?.length) return null;
  return problems
    .map((p) =>
      // eslint-disable-next-line i18next-templates/no-literal-string -- joins two already-translated lines
      p.hint ? `${p.message}\n${p.hint}` : p.message,
    )
    .join('\n\n');
}

/**
 * Merges the always-on pattern check with the on-demand Validate findings.
 *
 * `value_breaks_pattern` is ALWAYS dropped from the snapshot: the live check is
 * the authority on that particular fact, and it re-evaluates the value in the
 * cell right now. Filtering it only while the live check happens to be firing
 * meant that fixing the value made the stale message reappear — the tooltip
 * then quoted a value the cell no longer held.
 */
export function mergeViolation(
  live: string | null,
  problems: SchemaProblem[] | undefined,
): string | null {
  const rest = problems?.filter((p) => p.code !== 'value_breaks_pattern');
  return [live, describeProblems(rest)].filter(Boolean).join('\n\n') || null;
}

/**
 * How loudly a cell or column should be painted. Errors win over warnings, and
 * the live pattern check is always an error — the API rejects that value.
 *
 * Without this every finding rendered destructive-red, so a duplicate display
 * value looked exactly like a missing code and the summary bar's "2 warnings,
 * 0 errors" contradicted the grid underneath it.
 */
export function violationSeverityOf(
  live: string | null,
  problems: SchemaProblem[] | undefined,
): 'error' | 'warning' | null {
  if (live) return 'error';
  if (!problems?.length) return null;
  return problems.some((p) => p.severity === 'error') ? 'error' : 'warning';
}

// Getters, not values: a resolved label would freeze at import.
export const GROUP_META: Record<
  TaxonomyFieldGroup,
  { label: string; emptyLabel: string; accent: string; dot: string }
> = {
  media: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldsEditor.mediaFields');
    },
    get emptyLabel() {
      return i18n.t('knowledge:taxonomy.fieldsEditor.noMediaFieldsYet');
    },
    accent: 'border-t-sky-400',
    dot: 'bg-sky-400',
  },
  content: {
    get label() {
      return i18n.t('knowledge:taxonomy.fieldsEditor.contentFields');
    },
    get emptyLabel() {
      return i18n.t('knowledge:taxonomy.fieldsEditor.noContentFieldsYet');
    },
    accent: 'border-t-amber-400',
    dot: 'bg-amber-400',
  },
};

export const DEFAULT_VALUE_WIDTH = 128;
export const DEFAULT_CODE_WIDTH = 72;
export const MIN_COL_WIDTH = 48;

/**
 * Floor for a group's width, driven by its HEADER rather than its columns.
 *
 * One field is only 200px of columns (128 + 72), which isn't enough for
 * "● Content fields ⓵ + Add field" — the label wrapped to two lines and the
 * button ended up against the edge. Measured in the app, that header needs
 * 270px: 146 label + badge, 85 button, 16 gap, 24 cell padding. Rounded up for
 * breathing room so the button isn't flush against the border either.
 *
 * The same floor applies to the empty state, so two groups stay the same width
 * when one has a single field and the other has none.
 */
export const MIN_GROUP_WIDTH = 288;

export type Sub = 'value' | 'code';
export interface Selection {
  ar: number;
  ac: number;
  fr: number;
  fc: number;
}

export interface RowWindow {
  /** Absolute row indexes to render (the visible slice). */
  indexes: number[];
  /** Spacer heights (px) reserving the off-screen rows. */
  paddingTop: number;
  paddingBottom: number;
}
